import React from 'react';
import {BrowseCourse} from '@udemy-v2/browse-course';
import {Tag} from '@udemy-v2/react-product-card-components';
import {BrowseCourseCard} from '@udemy-v2/react-browse-course';
import {useContentLengthFormatter} from '@/features/course-landing-page/hooks/use-content-length-formatter.react-component';
import {useFormatNumber, LocalizedHtml, useI18n} from '@udemy-v2/i18n';
import {Ribbon} from '@udemy-v2/react-notice-components';
import {Icon} from '@udemy-v2/icons';
import {StarRating} from '@udemy-v2/react-merchandising-components';

import styles from './condensed-browse-course-card.module.scss';
import classNames from 'classnames';
import {useUserHasOrganization} from '@/lib/hooks/use-user-has-organization';

interface CondensedBrowseCourseCardProps {
    course: BrowseCourse;
}

export const NumLearnersRibbon = ({count, className}: {count: number; className?: string}) => {
    const {formatNumber} = useFormatNumber();
    const formattedCount = formatNumber(count);
    return (
        <Ribbon className={classNames(styles.gray_ribbon, className)}>
            <Icon color="inherit" size="xsmall" glyph="people" label={false} />
            {formattedCount}
        </Ribbon>
    );
};

export const RatingRibbon = ({rating, className}: {rating: number; className?: string}) => (
    <Ribbon className={classNames(styles.gray_ribbon, className)}>
        <StarRating numeric={true} rating={rating} size="small" />
    </Ribbon>
);

export const CondensedBrowseCourseCard = ({course}: CondensedBrowseCourseCardProps) => {
    const formatContentLength = useContentLengthFormatter();
    const {gettext, locale, interpolate} = useI18n();
    const hasOrganization = useUserHasOrganization();
    const userLocale = (locale || 'en_US').replace('_', '-');
    const lastUpdatedDate = new Date(course.updatedOn || '').toLocaleDateString(userLocale, {
        year: 'numeric',
        month: 'numeric',
    });
    const lengthInMinutes = (course.durationInSeconds || 0) / 60;
    const formattedLength = formatContentLength(lengthInMinutes);

    return (
        <BrowseCourseCard
            course={course}
            layout="condensed"
            options={{hideDescription: true, openInNewTab: true, hidePrice: hasOrganization}}
            renderAuthors={() => null}
            renderTags={() => [
                ...(course.rating?.average
                    ? [<RatingRibbon key="rating" rating={course.rating.average} />]
                    : []),
                ...(course.enrollments?.count
                    ? [<NumLearnersRibbon key="num-learners" count={course.enrollments.count} />]
                    : []),
                <Tag key="content-length">{formattedLength}</Tag>,
                <Tag key="last-updated-date">
                    <LocalizedHtml
                        html={interpolate(
                            gettext('<span>Updated: %(date)s</span>'),
                            {
                                date: lastUpdatedDate,
                            },
                            true,
                        )}
                        interpolate={{
                            date: <span>{lastUpdatedDate}</span>,
                        }}
                    />
                </Tag>,
            ]}
        />
    );
};
